import { Box, Typography, Link, Chip } from '@mui/material';
import { DetailSection, DetailField } from './DetailSection';
import { standardLabel } from './standards';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { CK, statusChipSx } from '../portalStyles';

// AWS hands back a zero time for findings it never stamped; rendering it would
// print "1/1/1" in the drawer, so treat it as absent.
const fmt = (t) => {
  if (!t) return '';
  const d = new Date(t);
  if (Number.isNaN(d.getTime()) || d.getUTCFullYear() <= 1) return '';
  return d.toLocaleString();
};

const COMPLIANCE_INTENT = {
  PASSED: 'success',
  FAILED: 'error',
  WARNING: 'warning',
  NOT_AVAILABLE: 'neutral',
};

// CSPM / Security Hub finding body, laid out the AWS-console way: grouped
// sections (Finding details / Resource / Compliance / Remediation). Remediation
// is read from the ASFF `raw` payload, as before.
export default function CspmDetailBody({ finding }) {
  if (!finding) return null;

  let raw = null;
  try { raw = finding.raw ? JSON.parse(finding.raw) : null; } catch (_) { /* ignore */ }
  const rec = raw?.Remediation?.Recommendation;

  let standards = [];
  try { standards = finding.standardIds ? JSON.parse(finding.standardIds) : []; } catch (_) { /* ignore */ }

  return (
    <>
      <DetailSection title="Finding details">
        <Typography variant="body2" sx={{ mb: 1.5, color: CK.navy }}>
          {finding.description}
        </Typography>
        <DetailField label="Finding ID" value={finding.findingId} />
        <DetailField label="Account" value={finding.accountId} />
        <DetailField label="Region" value={finding.region} />
        <DetailField label="First observed" value={fmt(finding.firstObservedAt)} />
        <DetailField label="Last observed" value={fmt(finding.lastObservedAt)} />
      </DetailSection>

      <DetailSection title="Resource">
        <DetailField label="Resource type" value={finding.resourceType} />
        <DetailField label="Resource" value={finding.resource || finding.resourceId} />
      </DetailSection>

      {finding.complianceStatus && (
        <DetailSection title="Compliance">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Chip
              size="small"
              label={formatStatusLabel(finding.complianceStatus)}
              sx={statusChipSx(COMPLIANCE_INTENT[finding.complianceStatus] || 'neutral')}
            />
          </Box>
        </DetailSection>
      )}

      {standards.length > 0 && (
        <DetailSection title="Standards">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {standards.map((s) => (
              <Chip key={s} label={standardLabel(s)} size="small" sx={statusChipSx('neutral')} />
            ))}
          </Box>
        </DetailSection>
      )}

      {rec?.Text && (
        <DetailSection title="Remediation">
          <Typography variant="body2" sx={{ mb: 0.5, color: CK.navy, wordBreak: 'break-word' }}>{rec.Text}</Typography>
          {rec.Url && (
            <Link href={rec.Url} target="_blank" rel="noreferrer">View documentation</Link>
          )}
        </DetailSection>
      )}
    </>
  );
}
